import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import useRealTimeMessaging from '@/hooks/useRealtimeMessaging';
import { logger } from '@/services/logger';
import { RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

interface UseMultiReferenceValidationUpdaterProps {
    fieldPath: string;
    setValueWithDirtyState: (path: string, value: any, options?: { shouldDirty?: boolean }) => void;
    extendedDataCache: React.MutableRefObject<Map<string, any>>;
    newlyCreatedItemRef: React.MutableRefObject<{ primaryKey__?: string; validation__?: any; validation_warnings__?: any } | null>;
    setNewlyCreatedItem: (item: any) => void;
    hasNewlyCreatedItem: boolean;
}

export function useMultiReferenceValidationUpdater({
    fieldPath,
    setValueWithDirtyState,
    extendedDataCache,
    newlyCreatedItemRef,
    setNewlyCreatedItem,
    hasNewlyCreatedItem
}: UseMultiReferenceValidationUpdaterProps) {
    const queryClient = useQueryClient();
    const { getValues } = useFormContext();
    const referencedItemIdsRef = useRef<string[]>([]);

    const { subscribe, unsubscribe } = useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.VALIDATION_FINISHED_EVENT,
        identifier: undefined,
        eventHandler: async (data: any) => {
            if (!data?.validationResults) return;

            const validatedItemIds = Object.keys(data.validationResults);
            const referencedValidatedIds = referencedItemIdsRef.current.filter((id: string) =>
                validatedItemIds.includes(id.toString())
            );

            // When no referenced items were validated, check if the newly created item (e.g. from Create New) was validated
            if (referencedValidatedIds.length === 0) {
                const newlyCreated = newlyCreatedItemRef.current;
                const newlyCreatedId = newlyCreated?.primaryKey__?.toString();
                if (newlyCreated && newlyCreatedId && validatedItemIds.includes(newlyCreatedId)) {
                    const result = data.validationResults[newlyCreatedId];
                    if (result) {
                        setNewlyCreatedItem({
                            ...newlyCreated,
                            validation__: result.validationErrors ?? newlyCreated.validation__,
                            validation_warnings__: result.validationWarnings ?? newlyCreated.validation_warnings__,
                            validation_dirty__: false
                        });
                        logger.debug('MultiReference validation updater: Updated validation state for newly created item:', newlyCreatedId);
                    }
                }
                return;
            }

            logger.debug('MultiReference validation updater: Processing validation updates for items:', referencedValidatedIds);

            try {
                let hasUpdates = false;

                // Use validation results from the event payload instead of fetching items again
                const results = referencedValidatedIds
                    .map((id: string) => {
                        const result = data.validationResults[id];
                        if (!result) return { id, validationResult: null, validationChanged: false };

                        const cachedItem = extendedDataCache.current.get(id);
                        if (!cachedItem) return { id, validationResult: result, validationChanged: false };

                        // Check if validation state actually changed to avoid unnecessary updates
                        const validationChanged =
                            JSON.stringify(cachedItem.validation__) !== JSON.stringify(result.validationErrors) ||
                            JSON.stringify(cachedItem.validation_warnings__) !== JSON.stringify(result.validationWarnings);

                        if (validationChanged) {
                            const updatedCachedItem = {
                                ...cachedItem,
                                validation__: result.validationErrors,
                                validation_warnings__: result.validationWarnings,
                                validation_dirty__: false
                            };

                            extendedDataCache.current.set(id, updatedCachedItem);

                            logger.debug('MultiReference validation updater: Updated validation state for item:', id);
                            return { id, validationResult: result, validationChanged: true };
                        }

                        return { id, validationResult: result, validationChanged: false };
                    });

                // Update React Query cache with validation data from the event
                results.forEach(({ id, validationResult }) => {
                    if (!validationResult) return;
                    const allQueries = queryClient.getQueriesData({ queryKey: ['items'] });
                    allQueries.forEach(([key, oldData]: any) => {
                        if (!oldData) return;

                        const newPages = oldData.pages.map((page: any) => ({
                            ...page,
                            data: page.data.map((item: any) =>
                                item.primaryKey__ === id
                                    ? {
                                        ...item,
                                        validation__: validationResult.validationErrors,
                                        validation_warnings__: validationResult.validationWarnings
                                    }
                                    : item
                            ),
                        }));

                        queryClient.setQueryData(key, { ...oldData, pages: newPages });
                    });
                });

                // Check if any validation actually changed
                hasUpdates = results.some(result => result.validationChanged);

                if (hasUpdates) {
                    // Get current form values to preserve order and structure
                    const currentFormValues = getValues(fieldPath) as any[] || [];

                    if (Array.isArray(currentFormValues) && currentFormValues.length > 0) {
                        // Map over current form values and update with cached validation data
                        const updatedItems = currentFormValues.map((formItem: any) => {
                            if (!formItem?.primaryKey__) return formItem;

                            const cachedItem = extendedDataCache.current.get(formItem.primaryKey__);
                            if (cachedItem) {
                                // Merge cached validation data into form item so ValidationStateCell can display it
                                return {
                                    ...formItem,
                                    validation__: cachedItem.validation__,
                                    validation_warnings__: cachedItem.validation_warnings__,
                                    validation_dirty__: false
                                };
                            }
                            return formItem;
                        });

                        setValueWithDirtyState(fieldPath, updatedItems, { shouldDirty: false });
                        logger.debug('MultiReference validation updater: Triggered form update for field:', fieldPath);
                    }
                }
            } catch (error) {
                logger.error('Failed to fetch or update multireference item validation:', error);
            }
        },
    });

    const updateReferencedItemIds = useCallback((items: any[]) => {
        if (items && Array.isArray(items)) {
            const itemIds = items.filter(item => item?.primaryKey__)
                .map(item => item.primaryKey__);
            if(itemIds) {
                // Only update if IDs actually changed
                const currentIdsString = [...itemIds].sort().join(',');
                const previousIdsString = [...referencedItemIdsRef.current].sort().join(',');

                if (currentIdsString !== previousIdsString) {
                    referencedItemIdsRef.current = itemIds;
                }
            }}
    }, []);

    useEffect(() => {
        const hasReferencedItems = referencedItemIdsRef.current.length > 0;
        if (!hasReferencedItems && !hasNewlyCreatedItem) return;

        subscribe(true);

    }, [referencedItemIdsRef.current.length, hasNewlyCreatedItem, subscribe, unsubscribe]);

    return {
        updateReferencedItemIds
    };
}
